import {
  type ColumnDef,
  type PaginationState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "./ui/skeleton";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading?: boolean;
  pageCount?: number;
  pagination?: PaginationState;
  setPagination?: React.Dispatch<React.SetStateAction<PaginationState>>;
}

export function DataTable<TData, TValue>({
  data,
  columns,
  pageCount,
  pagination,
  setPagination,
  isLoading,
}: DataTableProps<TData, TValue>) {
  const table = useReactTable({
    data,
    columns,
    state: {
      pagination,
    },
    pageCount: pageCount,
    onPaginationChange: setPagination,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
  });

  const pageIndex = table.getState().pagination?.pageIndex ?? 1;

  if (isLoading)
    return (
      <Table>
        <TableHeader>
          <TableRow>
            {Array.from({ length: columns.length }).map((_, index) => (
              <TableHead key={index}>
                <Skeleton className="h-4 w-24" />
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }).map((_, rowIndex) => (
            <TableRow key={rowIndex}>
              {Array.from({ length: columns.length }).map((_, colIndex) => (
                <TableCell key={colIndex}>
                  {colIndex === 1 ? (
                    <Skeleton className="h-6 w-24 rounded-full" />
                  ) : colIndex === 2 ? (
                    <div className="flex items-center gap-1">
                      <Skeleton className="h-4 w-24" />
                    </div>
                  ) : colIndex === 3 ? (
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-4 w-24" />
                    </div>
                  ) : (
                    <Skeleton className="h-4 w-24" />
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-md text-xs">
        <Table className={"max-w-full overflow-y-scroll bg-transparent"}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                className="border-b border-b-white/10"
                key={headerGroup.id}
              >
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead className="text-dark-text-400" key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  className="border-b border-b-dark-grey-50 hover:bg-white/5"
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell className="py-4" key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination — paginationParams (and thus this pageIndex) is 1-indexed */}
      {!!pagination?.pageIndex && table.getPageCount() > 1 && (
        <div className="flex items-center justify-center space-x-2">
          <Button
            className="border bg-transparent"
            variant="outline"
            size="sm"
            onClick={() =>
              setPagination?.((p) => ({ ...p, pageIndex: 1 }))
            }
            disabled={pageIndex <= 1}
          >
            <ChevronsLeft />
          </Button>
          <Button
            className="bg-transparent"
            variant="outline"
            size="sm"
            onClick={() =>
              setPagination?.((p) => ({ ...p, pageIndex: p.pageIndex - 1 }))
            }
            disabled={pageIndex <= 1}
          >
            <ChevronLeft className="size-4" />
          </Button>

          <div className="flex items-center gap-1">
            <div className="text-sm font-medium">{pageIndex}</div>
            <div className="text-muted-foreground text-sm">of</div>
            <div className="text-sm font-medium">{table.getPageCount()}</div>
          </div>

          <Button
            className="bg-transparent"
            variant="outline"
            size="sm"
            onClick={() =>
              setPagination?.((p) => ({ ...p, pageIndex: p.pageIndex + 1 }))
            }
            disabled={pageIndex >= table.getPageCount()}
          >
            <ChevronRight className="size-4" />
          </Button>
          <Button
            className="bg-transparent"
            variant="outline"
            size="sm"
            onClick={() =>
              setPagination?.((p) => ({
                ...p,
                pageIndex: table.getPageCount(),
              }))
            }
            disabled={pageIndex >= table.getPageCount()}
          >
            <ChevronsRight className="size-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
